import React from 'react';
import { useTranslation } from 'react-i18next';
import { keyMap } from '@locales/lokalise/keyMap';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import CONSTANTS from 'components/shared/constants';
import { ENUM_CONSTANTS } from 'components/shared/enum.constants';
import { GENERAL_CONSTANTS } from 'components/shared/general.constants';
import { Attributes, EventTarget } from './types';

type SelectOption = {
  key: string;
  attributes: Attributes;
  name: string;
  value: string;
};

interface IProps {
  name: string;
  value: string | null;
  onChange: (e: EventTarget) => void;
  onSetChange?: (name: string, value: Array<string> | Record<string, string>) => void;
  getLabel: (attributes: Attributes, key: string) => string;
  disabled?: boolean;
  required: boolean;
  values?: Array<SelectOption>;
  options?: Array<{ key: string | number; attributes?: Attributes; name?: string; value?: string }>;
  attributes: Attributes;
  canBeEmptyValue?: boolean;
}

const Select = ({
  name,
  value,
  onChange,
  getLabel,
  disabled,
  required,
  values,
  attributes: fieldAttributes,
  options: defaultOptions,
}: IProps) => {
  const { i18n } = useTranslation();
  const { enumListClientSide, generalEnumListClient } = fieldAttributes;
  const enumOptions = enumListClientSide && ENUM_CONSTANTS[enumListClientSide];
  const generalOptions = generalEnumListClient && GENERAL_CONSTANTS[generalEnumListClient as keyof GENERAL_CONSTANTS];

  const options = (generalOptions || enumOptions || defaultOptions || values || []) as Array<SelectOption>;
  const isDisabled = disabled || fieldAttributes.readOnly;

  const filteredOptions = options
    .filter((opt) => {
      const { attributes = {} } = opt;
      const { enumItemClientSideExport = {} } = attributes;

      return enumItemClientSideExport.exclude !== true;
    })
    .sort((a, b) => {
      const prevOpt =
        a.attributes && a.attributes.enumItemClientSideExport
          ? a.attributes.enumItemClientSideExport.order || CONSTANTS.HIGHEST_SORT_ORDER
          : CONSTANTS.HIGHEST_SORT_ORDER;
      const nextOpt =
        b.attributes && b.attributes.enumItemClientSideExport
          ? b.attributes.enumItemClientSideExport.order || CONSTANTS.HIGHEST_SORT_ORDER
          : CONSTANTS.HIGHEST_SORT_ORDER;

      return prevOpt - nextOpt;
    });

  const selectValue = value ?? '';

  return (
    <select
      className="cc-input"
      name={name}
      value={selectValue}
      onChange={onChange}
      required={required}
      disabled={isDisabled}>
      <option value="">{` -- ${i18n.t(keyMap.formsChoose)} -- `}</option>
      {filteredOptions.map((opt) => (
        <option key={opt.value || opt.key} value={opt.value || opt.key}>
          {i18n.t(opt.name as KeyMapKeys) || getLabel(opt.attributes, opt.key)}
        </option>
      ))}
    </select>
  );
};

export { Select };
